import React, { useCallback, useMemo } from 'react'
import Autocomplete, { AutocompleteProps, AutocompleteRenderInputParams, AutocompleteRenderOptionState } from '@mui/material/Autocomplete'
import Paper, { PaperProps } from '@mui/material/Paper'
import Popper, { PopperProps } from '@mui/material/Popper'
import Grow from '@mui/material/Grow'
import { observer } from 'mobx-react-lite'
import useStore from 'reusable/mobx-store-utils'
import { GapAutocompleteData, GapAutocompleteOption, GapAutocompleteOptionExtended } from '../types/GapAutocompleteTypes'
import { GapAutocompleteSingleStore, GapAutocompleteSingleStoreProvider } from './GapAutocompleteSingleStore'
import { gapAutoCompleteRenderInput, gapAutocompleteNormaliseOptions, gapAutocompleteIsOptionEqualToValue, gapAutoCompleteRenderOption, gapAutocompleteFilterOptions, gapAutocompleteGetOptionLabel } from '../shared/GapAutocompleteShared'
import GapAutocompleteListbox from '../shared/GapAutocompleteListbox'
import { themeConfig } from '@gap/shared/theme'

export interface GapAutocompleteSingleProps extends Omit<AutocompleteProps<GapAutocompleteOption, undefined, false, undefined>,
   | 'multiple'
   | 'freeSolo'
   | 'onChange'
   | 'value'
   | 'renderInput'
   | 'renderTags'
   | 'getOptionLabel'
   | 'isOptionEqualToValue'
   | 'options'
   | 'defaultValue'
   | 'disableClearable'
> {
   /**
    * A GapAutocompleteOption array or GapAutocompleteHierarchy with categories and optional subcategories.
   */
   options: GapAutocompleteData
   onChange?: (value?: any) => any
   onNewOption?: (inputValue: string) => void
   value?: string
   label: string
   helperText?: string
   error?: boolean
   /**
    * Displays the Check Icon right-aligned next to selected options.
    * @default true
    */
   displaySelectedIcon?: boolean
   disableClearable?: boolean
   variant?: 'standard' | 'outlined' | 'filled'
   /**
    * Optional footer rendered inside the dropdown Paper, below the options list.
    */
   footer?: React.ReactNode
   /**
    * Cap (in px) for the dropdown popper width. By default the popper matches the
    * input width; set this to let the list be wider than a narrow input. Capped
    * to the viewport on small screens.
    */
   dropdownMaxWidth?: number
   /**
    * Animate the dropdown open/closed with the same Grow transition MUI's Menu
    * and the date picker use (default: instant, MUI's Autocomplete default).
    */
   dropdownTransition?: boolean
}

/**
 * Popper slot that gives the Autocomplete list the Grow (scale + fade) transition
 * used by MUI's Menu/Popover, so the dropdown expands and contracts instead of
 * snapping. The Autocomplete keeps this popper mounted and only toggles `open`,
 * so enabling `transition` animates both directions.
 */
function GrowDropdownPopper({ children, ownerState: _ownerState, ...popperProps }: PopperProps & { ownerState?: unknown }) {
   return (
      <Popper {...popperProps} transition>
         {({ TransitionProps }) => (
            <Grow {...TransitionProps} timeout="auto" style={{ transformOrigin: 'top left' }}>
               {children as React.ReactElement}
            </Grow>
         )}
      </Popper>
   )
}

export default observer(function GapAutocompleteSingle(props: GapAutocompleteSingleProps) {
   const { autoFocus, openOnFocus, size, value, options, helperText, error, label, disableCloseOnSelect, variant, disabled, disableClearable = false, readOnly, onChange, onNewOption, displaySelectedIcon = true, footer, dropdownMaxWidth, dropdownTransition } = props
   const gapAutocompleteSingleStore = useStore((sp) => new GapAutocompleteSingleStore(sp), { options, value, onChange, onNewOption })
   const normalisedOptions = useMemo(() => gapAutocompleteNormaliseOptions(options), [options])

   const PaperComponent = useMemo(() => {
      if (!footer) return undefined
      // forwardRef so the Grow transition (when enabled) can animate this node.
      return React.forwardRef(function FooterPaperComponent(paperProps: PaperProps, ref: React.Ref<HTMLDivElement>) {
         return <Paper
            {...paperProps}
            ref={ref}
            onMouseOver={gapAutocompleteSingleStore.setFooterActiveFromPointer}
            sx={{
               ...paperProps.sx,
               // Hide MUI's default "No options" message — the footer handles the empty state.
               '.MuiAutocomplete-noOptions': { display: 'none' },
               // While the footer is the active row (class toggled via slotProps.paper below), drop the
               // option's lingering highlight and hold it on the footer instead. Applying it to the footer's
               // ButtonBase (where its own :hover paints) keeps the hovered and held states pixel-identical
               // and matching the option hover colour.
               '&.gap-footer-active .MuiAutocomplete-option.Mui-focused': { backgroundColor: 'transparent' },
               '&.gap-footer-active [data-gap-autocomplete-footer] .MuiButtonBase-root': { backgroundColor: 'action.hover' },
            }}>
            {paperProps.children}
            <div data-gap-autocomplete-footer onMouseDown={gapAutocompleteSingleStore.preventBlur}>{footer}</div>
         </Paper>
      })
   }, [footer, gapAutocompleteSingleStore])

   const selectedOption = React.useMemo(() => {
      const option = gapAutocompleteSingleStore.findOptionById(options, value)
      return option ? option : null
   }, [value, gapAutocompleteSingleStore, options])

   const renderInput = useCallback(
      (params: AutocompleteRenderInputParams) => gapAutoCompleteRenderInput({ label, helperText, error, readOnly, variant })(params),
      [helperText, error, label, readOnly, variant]
   )

   const renderOption = useCallback(
      (props: React.HTMLAttributes<HTMLLIElement>, option: GapAutocompleteOptionExtended, state: AutocompleteRenderOptionState) =>
         gapAutoCompleteRenderOption(displaySelectedIcon, themeConfig.primary['200'])(props, option, state),
      [displaySelectedIcon]
   )

   return <GapAutocompleteSingleStoreProvider store={gapAutocompleteSingleStore}>
      <Autocomplete
         loading={props.loading}
         disabled={disabled}
         autoFocus={autoFocus}
         openOnFocus={openOnFocus}
         size={size}
         value={selectedOption}
         options={normalisedOptions}
         multiple={false}
         renderInput={renderInput}
         renderOption={renderOption}
         disableCloseOnSelect={!!disableCloseOnSelect}
         isOptionEqualToValue={gapAutocompleteIsOptionEqualToValue}
         onChange={gapAutocompleteSingleStore.onChange}
         onInputChange={gapAutocompleteSingleStore.onInputChange}
         getOptionLabel={gapAutocompleteGetOptionLabel}
         freeSolo={!!onNewOption}
         filterOptions={!!onNewOption ? gapAutocompleteFilterOptions : undefined}
         disableClearable={disableClearable}
         onClose={gapAutocompleteSingleStore.onClose}
         onHighlightChange={gapAutocompleteSingleStore.onHighlightChange}
         slots={{ paper: PaperComponent, ...(dropdownTransition ? { popper: GrowDropdownPopper } : {}) }}
         slotProps={{
            paper: { className: gapAutocompleteSingleStore.isFooterActive ? 'gap-footer-active' : undefined },
            listbox: { component: GapAutocompleteListbox },
            // MUI matches the popper to the input width via an inline style; an
            // inline style of our own overrides it, letting the list be wider
            // than a narrow input while still fitting small viewports. We also
            // pin placement to 'bottom-start' — MUI leaves it at the Popper
            // default of 'bottom', which centres a wider-than-input list on the
            // anchor instead of left-aligning it.
            ...(dropdownMaxWidth ? { popper: { placement: 'bottom-start', style: { width: `min(${dropdownMaxWidth}px, calc(100vw - 32px))` } } } : {}),
         }}
         sx={props.sx}
      />
   </GapAutocompleteSingleStoreProvider>
})

/**
 * Quickly convert an enum into a autocomplete option list
 * @param myEnum Your enum
 * @param labelEnum Optional label object that must be Record<YourEnum, string>
 * @returns GapAutocompleteOption[]
 * @example
 * // Your backend code
 * export const docReminderEnum = ['none', '0-days', '1-day', '2-days', '3-days'] as const
 * export const docReminderLabel: Record<DocReminderEnum, string> = {
 *     "0-days": 'On the due date (9am)',
 *     "1-day": '1 day before (9am)',
 *     "2-days": '2 days before (9am)',
 *     "3-days": '3 days before (9am)',
 *     "none": 'None'
 *  }
 *
 *  // Your frontend code
 *  const options = enumToAutoCompleteOption(docReminderEnum, docReminderLabel))
 *  const component = <GapAutocompleteSingle options={options} />
 */
export const enumToAutoCompleteOption = <EnumType extends readonly string[]>(myEnum: EnumType, labelEnum?: Record<EnumType[number], string>): GapAutocompleteOption<EnumType[number]>[] => {
   return myEnum.map((item: EnumType[number]) => ({
      id: item,
      label: labelEnum ? labelEnum[item] : item.charAt(0).toUpperCase() + item.slice(1)
   }))
}
